import { useAppSelector } from '@store/hooks';
import { useCreateFromMemberManuallyMutation } from '@store/rtk/members';
import { useLazyGetBusinessTypesQuery } from '@store/rtk/selects';
import { selectSelects } from '@store/slices/selects/selects';
import { InputCalendarIcon } from '@src/svg';
import {
  ControlledDateSelect,
  ControlledInput,
  ControlledSelect,
} from '@uikit/FormItems/ControlledItems';
import SubmitButton from '@uikit/FormItems/SubmitButton/SubmitButton';
import { encrypt } from '@src/utils';
import { DEFAULT_PAGE_SIZE, X_HEADER_ID_NAME } from '@utils/constants';
import { sessionSetter, updateXHeaderId } from '@utils/storage';
import { validations } from '@utils/validations';
import Modal from '@uikit/Modal/Modal';
import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { logger } from '@utils/logger';
import { toastError } from '@utils/helpers';
import { toastMessage } from '@utils/toastMessage';

interface IEligibilityHistoryModal {
  success?: (message: string) => void;
  error?: (message: string) => void;
  closeModal: (bool: boolean) => void;
  memberData: any;
  createEligibilityHistoryModal: boolean;
}

const EligibilityHistoryModal: FC<IEligibilityHistoryModal> = ({
  closeModal,
  memberData,
  success,
}) => {
  const [getBusinessTypes] = useLazyGetBusinessTypesQuery();
  const { businessTypes } = useAppSelector(selectSelects);

  const {
    formState: { isSubmitting, isValid },
    control,
    handleSubmit,
    trigger,
    watch,
  } = useForm<any>({
    mode: 'all',
  });

  const [createMemberManually] = useCreateFromMemberManuallyMutation();
  const handleCreateManually = async (values) => {
    try {
      const res = await createMemberManually({
        body: {
          ...memberData,
          dob: memberData?.dob,
          eligibilityHistory: {
            ...values,
            lineOfBusiness: values.lineOfBusiness.id,
          },
        },
      }).unwrap();
      if (res) {
        updateXHeaderId('member', res.id);
        const getUpdatedXheaderData = sessionSetter.getItem(X_HEADER_ID_NAME);
        const encryptAll = encrypt({ member: getUpdatedXheaderData });
        window.open(`/members/information/${encryptAll}`, '_blank');
        success && success('Create successfully');
        closeModal(false);
      }
    } catch (err) {
      toastError(err, toastMessage.MEMBER.failedToCreateMember);
      logger.error(err);
    }
  };

  return (
    <>
      <Modal
        title="Eligibility Transaction"
        closeClick={() => closeModal(false)}
        maxWidth={'385px'}
      >
        <div
          data-test-id="hm-eligibility-history-modal"
          className="create-eligibility-modal-body"
        >
          <div
            className="date-select-part"
            data-test-id="modal-date-select-part"
          >
            <ControlledDateSelect
              dataTestId="modal-date-select-part-effective-date"
              control={control}
              name="effectiveDate"
              title="Start Date"
              IconLeft={<InputCalendarIcon />}
              rules={{
                required: validations.required('Start Date'),
              }}
              maxDate={new Date(watch('expirationDate'))}
              placeholder="MM/DD/YYYY"
            />
            <ControlledDateSelect
              dataTestId="modal-date-select-part-expiration-date"
              control={control}
              name="expirationDate"
              title="End Date"
              IconLeft={<InputCalendarIcon />}
              rules={{
                required: validations.required('End Date'),
              }}
              minDate={new Date(watch('effectiveDate'))}
              placeholder="MM/DD/YYYY"
            />
          </div>
          <div
            className="bottom-fields-part"
            data-test-id="modal-bottom-fields-part"
          >
            <ControlledInput
              dataTestId="modal-plan-code"
              name="planCode"
              control={control}
              info={{ desc: '', toggle: true }}
              rules={{
                required: validations.required('Plan Code'),
              }}
              title="Plan Code"
              type="alphanumeric"
            />
            <ControlledInput
              dataTestId="modal-group-code"
              name="groupCode"
              control={control}
              trigger={trigger}
              info={{ desc: '', toggle: true }}
              rules={{
                required: validations.required('Group Code'),
              }}
              title="Group Code"
              type="alphanumeric"
            />
            <ControlledSelect
              dataTestId="modal-line-of-business"
              control={control}
              name="lineOfBusiness"
              rules={{
                validate: validations.selectRequired('Line of Business'),
              }}
              title="Line of Business"
              selectOptions={businessTypes}
              requestByAsync={() =>
                getBusinessTypes({ page: 0, size: DEFAULT_PAGE_SIZE })
              }
              placeholder="Select"
            />
          </div>
          <div
            className="member-create-submit-button"
            data-test-id="modal-submit-button"
          >
            <SubmitButton
              dataTestId="modal-submit-button-btn"
              className="create-modal-btn"
              title="Save"
              loading={isSubmitting}
              disabled={isSubmitting}
              onClick={handleSubmit(handleCreateManually)}
              forceDisable={!isValid}
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default EligibilityHistoryModal;
